/**
 * 日次【集計】(⑥指示: 日次／週次／月次を切替)。現行シート「日次【集計】」の再現。
 *  親=アポインター ／ 子=架電会社(期間内の同じ会社を合算)。行をクリックすると子行を開閉(既定は閉じる。⑦指示)
 *  列の並びは現行シートに合わせる(⑪指示 D-40): RANK／アポインター／架電会社／架電内容／取得数 → 時間別 → 通話・取得効率 → BB稼働 → 売上・報酬・原価率
 *   **出勤形態・報酬(単価)の列は出さない**(2026-09-24 発注者指示。D-134。出勤形態は絞り込みと既定の並び順には残す)
 *  先頭2列は横スクロールしても固定。列グループの境に縦線。説明の吹き出しは見出しだけ(表の中には出さない)
 *  最下部に現行シートと同じ区分別合計(出勤／不用品／在宅／合計。「ホワイト」は不用品と呼ぶ。D-134)
 * 2026-09-18 UIデザインシステムの集計表に置き換え(D-90): 色は colors.ts の基準で「良い=緑の太字 / 目標超過=赤字と印」、RANK と見込/h は評価段階の色。
 * 画面の高さに収め、表の枠の中だけスクロールする。
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import HelpTip, { type Help } from '@core/components/HelpTip';
import { IconChevronRight } from '@core/components/icons';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import Panel from '@core/components/ui/Panel';
import RankBadge, { rankTextClass } from '@core/components/ui/RankBadge';
import SortableTh from '@core/components/ui/SortableTh';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import StatusBadge from '@core/components/ui/StatusBadge';
import TableDensity, { useTableDensity } from '@core/components/ui/TableDensity';
import ToneValue from '@core/components/ui/ToneValue';
import CsvUploadModal from '@core/import/CsvUploadModal';
import { MANAGER_HOURLY } from '@core/import/buildDailySummary';
import { resolveCompanyByCode } from '@core/import/activity';
import { fetchCallWindows } from '@core/import/bbApiRepo';
import { coefficientOf, estimateHours, styleCoefficient, windowHours, windowsByBbId, type CallWindow } from '@core/import/workEstimate';
import { fetchOperators } from '@core/masters/operatorRepo';
import { BUSINESS_LABELS, HOUR_BANDS, type BusinessType, type DateStr, type Operator } from '@core/lib/types';
import { formatCount, formatCurrency, formatDecimal, formatDuration, formatPercent } from '@core/utils/formatters';
import KintaiModal, { fmtKintaiCheck } from './KintaiModal';
import { addDays, apphOf, derived, groupByAppointer, rankOf, styleOf, sumAgg, type Aggregate, type AppointerGroup, type Rank, type StaffMetrics } from './calc';
import { HELP, costRateTone, hitRateTone, hourlyTone, talkPerApptTone, workPerApptTone } from './colors';
import PeriodToolbar from './PeriodToolbar';
import { buildLiveStaffRows, liveAsOf, type LiveApiDoc } from './liveStaffRows';
import { DEFAULT_DISPLAY, fetchStaffSummaryDisplay, type StaffSummaryDisplay } from './staffSummaryDisplay';
import { fetchSummariesRange } from './dashboardRepo';
import { fetchBbApiDaily, fetchBbApiLive } from '@core/import/bbApiRepo';
import { todayStr } from '@core/lib/date';
import { usePeriodData } from './usePeriod';

type SortKey =
  | 'rank'
  | 'name'
  | 'style'
  | 'wage'
  | 'company'
  | 'business'
  | 'appointments'
  | 'calls'
  | 'api_calls'
  | 'hit_rate'
  | 'talk_per_appt'
  | 'work_per_appt'
  | 'apph'
  | 'reception'
  | 'talk'
  | 'after'
  | 'work'
  | 'hours'
  | 'est_hours'
  | 'calls_per_hour'
  | 'unit'
  | 'revenue'
  | 'pay'
  | 'cost_rate';

/** 子行: 期間内の同じ架電会社を合算した1行 */
type CompanyChild = {
  company_id: string | null;
  style: string;
  business: BusinessType;
  is_manager: boolean;
  wage: number | null;
  /** 報酬区分(時給 /h・件数単価 /件 の表示に使う)。混在は null */
  commission: boolean | null;
  unit_price: number | null;
  days: number;
  agg: Aggregate;
  /** 合算前の行(日次なら1行 = BB ID × 会社 × 日) */
  rows: StaffMetrics[];
};

function childrenByCompany(g: AppointerGroup): CompanyChild[] {
  const map = new Map<string, StaffMetrics[]>();
  for (const r of g.children) map.set(r.company_id ?? '', [...(map.get(r.company_id ?? '') ?? []), r]);
  return [...map.values()].map((list) => {
    const first = list[0]!;
    const uniq = <T,>(pick: (r: StaffMetrics) => T) => Array.from(new Set(list.map(pick)));
    const wages = uniq((r) => r.wage);
    const units = uniq((r) => r.unit_price);
    const payTypes = uniq((r) => r.pay_type);
    return {
      company_id: first.company_id,
      style: uniq(styleOf).join('／'),
      business: (list.every((r) => r.business === 'fuyohin') ? 'fuyohin' : 'kyutoki') as BusinessType,
      is_manager: first.is_manager,
      wage: wages.length === 1 ? wages[0]! : null,
      commission: payTypes.length === 1 ? payTypes[0] === 'commission' : null,
      unit_price: units.length === 1 ? units[0]! : null,
      days: new Set(list.map((r) => r.date)).size,
      agg: sumAgg(list),
      rows: list,
    };
  });
}

/** 出勤形態の並び順(⑧指示): 管理者 → 品川 → タイミー → 在宅時給 → 在宅歩合。同じ形態では給湯器 → 不用品 */
const STYLE_ORDER: Record<string, number> = { '管理/時給': 0, '出勤/時給': 1, 'タイミー/時給': 2, '在宅/時給': 3, '在宅/歩合': 4 };
function styleRank(style: string, business: BusinessType): number {
  const first = style.split('／')[0] ?? style;
  return (STYLE_ORDER[first] ?? 9) + (business === 'fuyohin' ? 0.5 : 0);
}

/** RANKの並べ替え値: S>A>B+>B->C の順、同じRANK内は見込/h(不用品は10倍で判定した値) */
function rankScore(rank: Rank, agg: Aggregate, business: BusinessType): number {
  const order: Record<Rank, number> = { S: 5, A: 4, 'B+': 3, 'B-': 2, C: 1, '—': 0 };
  const apph = apphOf(agg) ?? 0;
  return order[rank] * 1000 + (business === 'fuyohin' ? apph * 10 : apph);
}

function aggValue(agg: Aggregate, key: SortKey): number | null {
  const d = derived(agg);
  switch (key) {
    case 'appointments':
      return agg.appointments;
    case 'calls':
      return agg.calls;
    case 'hit_rate':
      return d.hit_rate;
    case 'talk_per_appt':
      return d.talk_per_appt;
    case 'work_per_appt':
      return d.work_per_appt;
    case 'apph':
      return d.apph;
    case 'reception':
      return agg.reception_sec;
    case 'talk':
      return agg.talk_sec;
    case 'after':
      return agg.after_sec;
    case 'work':
      return d.work_sec;
    case 'hours':
      return agg.hours;
    case 'calls_per_hour':
      return d.calls_per_hour;
    case 'revenue':
      return agg.revenue;
    case 'pay':
      return agg.pay;
    case 'cost_rate':
      return d.cost_rate;
    default:
      return null;
  }
}

/**
 * 最下部の区分別合計(現行シート「日次【集計】」の 品川／ホワイト／在宅／合計 の行)。
 *  出勤 = 品川・タイミー・管理者(給湯器) ／ **不用品**(現行シートの「ホワイト」。2026-09-24 発注者指示で呼び名を変更。D-134) ／ 在宅 = 在宅(時給・歩合)
 *  合計 = 出勤＋在宅(給湯器。RANK もこの値)。不用品を含めた全体は別行に出す
 */
type SubtotalKey = 'shinagawa' | 'white' | 'zaitaku';
const SUBTOTALS: { key: SubtotalKey; label: string }[] = [
  { key: 'shinagawa', label: '出勤／給湯器' },
  { key: 'zaitaku', label: '在宅／給湯器' },
  { key: 'white', label: '不用品' },
];
function subtotalOf(r: StaffMetrics): SubtotalKey {
  if (r.business === 'fuyohin') return 'white';
  return r.work_style === 'zaitaku' ? 'zaitaku' : 'shinagawa';
}

/** 固定する先頭2列の幅(px) */
const W_RANK = 64;
/** RANK のとなりの「取得実績」(旧名 見込/h。2026-09-22 D-124) */
const W_APPH = 64;
/**
 * RANK の右に出す原価率の列(2026-09-20 発注者指示。D-99)。
 * 100%を超える原価率(「276.2%」＋超過の印)でも「%」が切れないように 76px(2026-09-24 実測で修正)
 */
const W_COST = 76;
const W_NAME = 150;

export default function StaffSummaryPage() {
  const period = usePeriodData();
  const { rows, targets, companyMap, companies, summaries, loading, range, mode, reload } = period;
  const { isAdmin } = useAuth();
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [styleF, setStyleF] = useState<Set<string>>(new Set());
  const [bizF, setBizF] = useState<Set<BusinessType>>(new Set());
  const [companyF, setCompanyF] = useState<Set<string>>(new Set());
  const [keyword, setKeyword] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [density, setDensity] = useTableDensity('cc.staffSummary.density');
  /**
   * 色のしきい値(2026-09-24 発注者依頼。P-61)。**配色は現行スプレッドシートと同じに固定**し、
   * しきい値だけ 設定 → システム設定「日次【集計】の色」で変える(全員共通)
   */
  const [display, setDisplay] = useState<StaffSummaryDisplay>(DEFAULT_DISPLAY);
  useEffect(() => {
    let alive = true;
    void fetchStaffSummaryDisplay()
      .then((d) => alive && setDisplay(d))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  /** 出勤の勤怠の確認モーダル(日次・OPレポート取込済みの日だけ) */
  const [kintaiOpen, setKintaiOpen] = useState(false);
  /** 架電会社別(子行)を開けるようにするか。**1社だけの人は開けない**(2026-09-20 発注者指示。D-102) */
  const [showByCompany, setShowByCompany] = useState(true);
  // 既定の並びは出勤形態(管理者→品川→在宅時給→在宅歩合)。同じ形態の中は見込/h の高い順(groupByAppointer の順)
  const { sort, toggle: toggleSort } = useSort<SortKey>({ key: 'style', dir: 'asc' });
  const [params, setParams] = useSearchParams();
  /** CSV取込前に表を埋めるための BlueBean API の記録(日中=速報 / 19時以降=締め) */
  const [live, setLive] = useState<{ doc: LiveApiDoc; kind: '速報' | '締め' } | null>(null);
  /** 「◯時◯分に読み込みました」(リアルタイム表示の目印) */
  const [liveAt, setLiveAt] = useState<Date | null>(null);

  const companyName = (id: string | null) => (id ? (companyMap.get(id)?.name ?? id) : '割振なし');

  // ---- 実稼働(推定): 会社ごとの「最初の架電〜最後の架電」× その人の係数(参考列。docs/specs/bluebean-api.md §14) ----
  const [windows, setWindows] = useState<Map<DateStr, Map<string, Record<string, CallWindow>>>>(new Map());
  const [operators, setOperators] = useState<Operator[]>([]);
  useEffect(() => {
    let alive = true;
    void fetchOperators()
      .then((o) => alive && setOperators(o))
      .catch(() => alive && setOperators([]));
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    let alive = true;
    void fetchCallWindows(range.from, range.to)
      .then((w) => alive && setWindows(w))
      .catch(() => alive && setWindows(new Map()));
    return () => {
      alive = false;
    };
  }, [range.from, range.to]);

  /** BB ID × 日付 × 会社 → 推定の実稼働(h)。係数はその人の他の日の実績(2日未満なら出勤形態の平均) */
  const estByKey = useMemo(() => {
    const out = new Map<string, number>();
    // 確定(rows)が無い速報の日でも出す。係数の材料が無いときは出勤形態の既定値になる(D-121)
    if (windows.size === 0) return out;
    const bbOfInternal = new Map<string, string>();
    for (const o of operators) for (const a of o.bb_accounts) if (a.bb_internal_id) bbOfInternal.set(String(a.bb_internal_id), a.bb_id);
    // 日ごとの窓(BB ID 単位)と、確定した実稼働
    const windowByBbDate = new Map<string, { total: number; byCode: Record<string, number> }>();
    for (const [date, byInternal] of windows) {
      for (const [bb, codes] of windowsByBbId(byInternal, bbOfInternal)) {
        const byCode: Record<string, number> = {};
        let total = 0;
        for (const [code, w] of Object.entries(codes)) {
          const h = windowHours(w);
          byCode[code] = h;
          total += h;
        }
        windowByBbDate.set(`${bb}|${date}`, { total, byCode });
      }
    }
    const workedByBbDate = new Map<string, number>();
    // 出勤形態はマスタから引く(確定した行が無い日でも係数の既定値を選べるように)
    const styleOfBb = new Map<string, { work_style: StaffMetrics['work_style']; pay_type: StaffMetrics['pay_type'] }>();
    for (const o of operators) for (const a of o.bb_accounts) styleOfBb.set(a.bb_id, { work_style: a.work_style, pay_type: a.pay_type });
    for (const r of rows) workedByBbDate.set(`${r.bb_id}|${r.date}`, (workedByBbDate.get(`${r.bb_id}|${r.date}`) ?? 0) + r.worked_hours);
    /** その人の係数(その日は材料に入れない) */
    const coefOf = (bb: string, date: DateStr) => {
      const history: { date: DateStr; worked: number; window: number }[] = [];
      for (const [key, w] of windowByBbDate) {
        const [b, d] = key.split('|') as [string, DateStr];
        if (b !== bb || d === date) continue;
        history.push({ date: d, worked: workedByBbDate.get(key) ?? 0, window: w.total });
      }
      const st = styleOfBb.get(bb);
      return coefficientOf(history, st ? styleCoefficient(st.work_style, st.pay_type) : 0.9).value;
    };
    for (const [key, w] of windowByBbDate) {
      const [bb, date] = key.split('|') as [string, DateStr];
      const coef = coefOf(bb, date);
      for (const [code, h] of Object.entries(w.byCode)) {
        const company = resolveCompanyByCode(code, companies);
        const k = `${bb}|${date}|${company?.id ?? ''}`;
        out.set(k, (out.get(k) ?? 0) + estimateHours(h, coef));
      }
    }
    return out;
  }, [windows, operators, rows, companies]);

  /**
   * BB ID × 日付 × 会社 → **その会社への架電数**(BlueBean API の記録)。
   * オペレーターレポートの通話回数は人ごとの合計しか無く、2社に架電した日は1社目に寄ってしまうため、
   * 会社別はこちら(APIの記録)を出す(2026-09-20 発注者指示。D-102)
   */
  const callsByKey = useMemo(() => {
    const out = new Map<string, number>();
    if (windows.size === 0) return out;
    const bbOfInternal = new Map<string, string>();
    for (const o of operators) for (const a of o.bb_accounts) if (a.bb_internal_id) bbOfInternal.set(String(a.bb_internal_id), a.bb_id);
    for (const [date, byInternal] of windows) {
      for (const [bb, codes] of windowsByBbId(byInternal, bbOfInternal)) {
        for (const [code, w] of Object.entries(codes)) {
          const company = resolveCompanyByCode(code, companies);
          const k = `${bb}|${date}|${company?.id ?? ''}`;
          out.set(k, (out.get(k) ?? 0) + (w.talked ?? 0));
        }
      }
    }
    return out;
  }, [windows, operators, companies]);

  /** その行の「会社別の架電数」。APIの記録が無い行が混ざっていたら null(足りない分だけ小さく出るため) */
  const callsOf = (list: StaffMetrics[]): number | null => {
    if (callsByKey.size === 0) return null;
    const seen = new Set<string>();
    let total = 0;
    for (const r of list) {
      const key = `${r.bb_id}|${r.date}|${r.company_id ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const v = callsByKey.get(key);
      if (v !== undefined) total += v;
      else if (r.calls > 0 || r.appointments > 0) return null; // 架電はあるのに API の記録が無い = 足りない
    }
    return total;
  };

  /**
   * その行(親・子・合計)の推定実稼働。
   * 架電があるのに API の窓が無い行が1つでもあれば **null**(足りない分だけ小さく出て、実稼働と比べられなくなるため)
   */
  const estimateOf = (list: StaffMetrics[]): number | null => {
    if (estByKey.size === 0) return null;
    const seen = new Set<string>();
    let total = 0;
    for (const r of list) {
      const key = `${r.bb_id}|${r.date}|${r.company_id ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const v = estByKey.get(key);
      if (v !== undefined) total += v;
      else if (r.calls > 0 || r.appointments > 0) return null; // 架電はあるのに窓が無い = API の結果が足りない
    }
    return Math.round(total * 100) / 100;
  };

  /**
   * **速報(CSV取込前)の、ここまでの報酬額**(2026-09-24 発注者指示「リアルタイムの件数×単価での原価率」。D-135)。
   * 売上見込 = 取得件数 × 会社単価(実数)。これに対する原価は、その日の報酬をまだ取れないので次の見込で出す:
   *  - 歩合: 取得件数 × 件数単価(**実数**)
   *  - 時給: 時給 × 実稼働(推定)(最初の架電〜最後の架電 × その人の係数。D-93・D-105 と同じ)
   *  - 管理: 参考時給(MANAGER_HOURLY) × 実稼働(推定)。確定の集計と同じ扱いにして、合計の原価率がそろうようにする
   * 推定の材料(架電の窓)が無い行が混ざったら null(「—」)。
   * **見込みの表示だけに使い、保存・PL・報酬の確定には使わない**(確定は翌日のオペレーターレポート取込)
   */
  const livePayOf = (list: StaffMetrics[]): number | null => {
    const seen = new Set<string>();
    let total = 0;
    for (const r of list) {
      if (r.pay_type === 'commission') {
        total += (r.wage ?? 0) * r.appointments;
        continue;
      }
      const key = `${r.bb_id}|${r.date}|${r.company_id ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const est = estByKey.get(key);
      if (est === undefined) return null;
      total += (r.is_manager ? MANAGER_HOURLY : (r.wage ?? 0)) * est;
    }
    // 数字にならない材料が混ざったときは出さない(「—」のまま)
    return Number.isFinite(total) ? Math.round(total) : null;
  };

  /**
   * 会社別の行の報酬額見込(D-105・D-110)。日次集計に保存してある会社別の報酬をそのまま足す。
   *  - 歩合: 取得件数 × 件数単価(会社ごとの実数)
   *  - 時給: 1日ぶんの報酬(時給 × 実稼働)を、会社ごとの架電の窓の比で分けたもの
   * どちらも **会社別の合計 = 親の行の金額** になる(どこか1社に寄らない)
   */
  const childPay = (c: CompanyChild): number | null => c.agg.pay;

  const styleOptions = useMemo(() => Array.from(new Set(rows.map(styleOf))).sort().map((s) => ({ key: s, label: s })), [rows]);
  const companyOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.company_id ?? '')))
        .map((id) => ({ id, name: companyName(id || null) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, companyMap],
  );

  /**
   * 合計に入れないアポインター(op_id)。URL の `off` に持つので、リロードしても共有しても同じ見え方になる。
   * 外した人は行を薄く出したまま、**合計・小計・出勤形態別の集計から抜く**(2026-09-21 発注者指示。D-117)
   */
  const excluded = useMemo<Set<string>>(() => new Set((params.get('off') ?? '').split(',').filter(Boolean)), [params]);
  const setExcluded = (next: Set<string>) => {
    const p = new URLSearchParams(params);
    if (next.size === 0) p.delete('off');
    else p.set('off', [...next].join(','));
    setParams(p, { replace: true });
  };
  const toggleExcluded = (opId: string) => {
    const next = new Set(excluded);
    if (next.has(opId)) next.delete(opId);
    else next.add(opId);
    setExcluded(next);
  };

  // 日を選んでいるときだけ速報を読む。今日を見ているあいだは1分おきに取り直す(リアルタイム。D-120)
  useEffect(() => {
    if (mode !== 'day') {
      setLive(null);
      return;
    }
    let alive = true;
    const load = () =>
      void Promise.all([fetchBbApiDaily(range.anchor), fetchBbApiLive(range.anchor)])
        .then(([daily, liveDoc]) => {
          if (!alive) return;
          if (daily) setLive({ doc: daily as LiveApiDoc, kind: '締め' });
          // 19時の締めは 速報の文書を その日の終わり(23:59:59)まで取り直して入れ替える → 締めとして出す
          else if (liveDoc) setLive({ doc: liveDoc as LiveApiDoc, kind: (liveDoc as LiveApiDoc).window_to === '23:59:59' ? '締め' : '速報' });
          else setLive(null);
          setLiveAt(new Date());
        })
        .catch(() => alive && setLive(null));
    load();
    if (range.anchor !== todayStr()) return () => { alive = false; };
    const timer = window.setInterval(load, 60_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [mode, range.anchor]);

  /**
   * 直近14日の時間帯の分布(合計が1)。速報の「予測(着地)」に使う。
   * 予測 = いまの取得 ÷ その時台までに入っていた割合(D-122)
   */
  const [hourDist, setHourDist] = useState<number[] | null>(null);
  useEffect(() => {
    if (mode !== 'day') return;
    let alive = true;
    void fetchSummariesRange(addDays(range.anchor, -14) as DateStr, addDays(range.anchor, -1) as DateStr)
      .then((list) => {
        if (!alive) return;
        const sum = HOUR_BANDS.map(() => 0);
        for (const x of list) for (const st of x.staff) HOUR_BANDS.forEach((_, i) => (sum[i] = (sum[i] ?? 0) + (st.by_hour?.[i] ?? 0)));
        const t = sum.reduce((a, b) => a + b, 0);
        setHourDist(t > 0 ? sum.map((v) => v / t) : null);
      })
      .catch(() => setHourDist(null));
    return () => {
      alive = false;
    };
  }, [mode, range.anchor]);

  /** 速報から作った行(CSV取込前の日だけ使う) */
  const liveRows = useMemo(
    () => (targets ? buildLiveStaffRows({ doc: live?.doc ?? null, date: range.anchor, operators, companies, companyMap, targets }) : []),
    [live, range.anchor, operators, companies, companyMap, targets],
  );
  /** 確定(CSV)がまだ無く、速報で表を出している状態 */
  const isLive = mode === 'day' && rows.length === 0 && liveRows.length > 0;
  /** 表のもとになる行。確定があればそれ、無ければ速報 */
  const sourceRows = isLive ? liveRows : rows;

  /** 速報の「予測(着地)」。まだ2割も進んでいない時間帯では出さない */
  const liveForecast = useMemo(() => {
    if (!isLive || live?.kind !== '速報' || !hourDist) return null;
    const hour = Number((live.doc.window_to ?? '').slice(0, 2) || new Date().getHours());
    let idx = -1;
    HOUR_BANDS.forEach((h, i) => {
      if (h <= hour) idx = i;
    });
    if (idx < 0) return null;
    const share = hourDist.slice(0, idx + 1).reduce((a, b) => a + b, 0);
    const appts = liveRows.reduce((a, r) => a + r.appointments, 0);
    if (share < 0.2 || appts === 0) return null;
    return Math.round(appts / share);
  }, [isLive, live, hourDist, liveRows]);

  const filteredRows = useMemo(
    () =>
      sourceRows.filter(
        (r) => matchesFilter(styleF, styleOf(r)) && matchesFilter(bizF, r.business) && matchesFilter(companyF, r.company_id ?? '') && matchKeyword(keyword, r.name, r.op_code, r.bb_id),
      ),
    [sourceRows, styleF, bizF, companyF, keyword],
  );

  const groupWage = (g: AppointerGroup) => (g.is_manager ? null : Array.from(new Set(g.children.map((r) => r.wage))).length === 1 ? g.children[0]!.wage : null);
  const groupUnit = (g: AppointerGroup) => (Array.from(new Set(g.children.map((r) => r.unit_price))).length === 1 ? g.children[0]!.unit_price : null);

  const groups = useMemo(() => {
    const base = groupByAppointer(filteredRows).map((g) => ({ g, children: childrenByCompany(g) }));
    const sorted = sortRows(base, sort, ({ g, children }, key) => {
      if (key === 'name') return g.name;
      if (key === 'style') return styleRank(g.style, g.business);
      if (key === 'wage') return groupWage(g);
      if (key === 'unit') return groupUnit(g);
      if (key === 'business') return g.businesses;
      if (key === 'company') return children.length === 1 ? companyName(children[0]!.company_id) : `${children.length}社`;
      if (key === 'rank') return rankScore(g.rank, g.agg, g.business);
      if (key === 'est_hours') return estimateOf(g.children);
      if (key === 'api_calls') return callsOf(g.children);
      return aggValue(g.agg, key);
    });
    return sorted.map(({ g, children }) => ({
      g,
      children: sortRows(children, sort ?? { key: 'appointments', dir: 'desc' }, (c, key) => {
        if (key === 'name') return g.name;
        if (key === 'style') return styleRank(c.style, c.business);
        if (key === 'wage') return c.wage;
        if (key === 'unit') return c.unit_price;
        if (key === 'business') return BUSINESS_LABELS[c.business];
        if (key === 'company') return companyName(c.company_id);
        if (key === 'rank') return rankScore(rankOf(apphOf(c.agg), c.business), c.agg, c.business);
        if (key === 'est_hours') return estimateOf(c.rows);
        if (key === 'api_calls') return callsOf(c.rows);
        return aggValue(c.agg, key);
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, sort, companyMap]);

  /** 合計に入れる行(外したアポインターを抜いたもの) */
  const countedRows = useMemo(() => (excluded.size === 0 ? filteredRows : filteredRows.filter((r) => !excluded.has(r.op_id))), [filteredRows, excluded]);
  /** 画面に出ている人のうち、合計から外している人数 */
  const excludedCount = useMemo(() => new Set(filteredRows.filter((r) => excluded.has(r.op_id)).map((r) => r.op_id)).size, [filteredRows, excluded]);

  const total = useMemo(() => sumAgg(countedRows), [countedRows]);
  const subtotals = useMemo(() => {
    const by = new Map<SubtotalKey, StaffMetrics[]>();
    for (const r of countedRows) by.set(subtotalOf(r), [...(by.get(subtotalOf(r)) ?? []), r]);
    const kyutoki = countedRows.filter((r) => r.business !== 'fuyohin');
    return {
      rows: SUBTOTALS.map((s) => ({ ...s, agg: sumAgg(by.get(s.key) ?? []), rows: by.get(s.key) ?? [] })),
      kyutoki: sumAgg(kyutoki),
      hasWhite: (by.get('white') ?? []).length > 0,
    };
  }, [countedRows]);
  const callRate = targets?.call_rate_per_min ?? 2.88;
  // 原価率の赤: 給湯器=cost_rate_total(既定45%) / 不用品=cost_rate_fuyohin(既定50%)。月次設定で変えられる(2026-09-24)
  const costTarget = targets?.cost_rate_total ?? null;
  const fuyohinTarget = targets?.cost_rate_fuyohin ?? null;
  const targetOf = (business: BusinessType | null | undefined) => (business === 'fuyohin' ? fuyohinTarget : costTarget);

  const toggleOpen = (id: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** RANK・取得実績・原価率・アポインター・架電会社・架電内容・取得数(出勤形態・報酬は出さない。D-134) */
  const BASIC = 7;
  /** 通話・取得効率 4列(見込/h は左へ移した)／BB稼働 10列(受付 AVG. を足した)／売上 4列／合計から外す 1列 */
  const colCount = BASIC + HOUR_BANDS.length + 4 + 10 + 4 + 1;

  /** 出勤(品川・タイミー)で OPレポートの稼働がある行(勤怠の確認の対象)。日次だけ */
  const isShukkin = (r: StaffMetrics) => (r.work_style === 'shinagawa' || r.work_style === 'timee') && r.shift_hours !== null;
  const shukkinRows = useMemo(() => (mode === 'day' ? rows.filter(isShukkin) : []), [rows, mode]);
  /** 親(アポインター)行・子(会社)行が日次の出勤1行だけを表すとき、その行(実稼働の吹き出しと「手」の印に使う) */
  const shukkinOf = (children: CompanyChild[]): StaffMetrics | undefined => {
    if (mode !== 'day' || children.length !== 1) return undefined;
    const c = children[0]!;
    return c.rows.length === 1 && !c.is_manager && isShukkin(c.rows[0]!) ? c.rows[0] : undefined;
  };
  const kintaiCheck = mode === 'day' ? summaries[0]?.kintai_check : undefined;
  /** その日に OPレポートが取込済みか(勤怠の確認はその後) */
  const hasOpReport = mode === 'day' && !!summaries[0]?.sources?.op_report;
  const activitySources = mode === 'day' ? (summaries[0]?.sources?.activity ?? []) : [];
  /** 週次・月次でボタンを押したら日次に切り替えてから開く(常設。2026-09-10 指示) */
  const [kintaiPending, setKintaiPending] = useState(false);
  useEffect(() => {
    if (kintaiPending && mode === 'day' && !loading) {
      setKintaiPending(false);
      setKintaiOpen(true);
    }
  }, [kintaiPending, mode, loading]);
  const openKintai = () => {
    if (mode !== 'day') {
      period.setMode('day');
      setKintaiPending(true);
    } else {
      setKintaiOpen(true);
    }
  };

  /**
   * 取得数より右の列(時間別／通話・取得効率／BB稼働／売上・報酬・原価率)。色は colors.ts の基準、吹き出しは出さない。
   * 合計行(isTotal)は現行シートと同じく AVG. の欄に通話料(通話分 × 通話料単価)を出す
   */
  const metricCells = (
    agg: Aggregate,
    unit: number | null,
    isTotal = false,
    shukkin?: StaffMetrics,
    est: number | null = null,
    apiCalls: number | null = null,
    /**
     * **架電会社別の子行**(2026-09-20 発注者指示。D-104)。
     * 親行は オペレーターレポート の数字、子行は **BlueBean API の記録** を出す。
     * 受付・通話時間・後処理・稼働・報酬は **人ごとの合計しか無く会社別に割れない**ので「—」にする
     */
    perCompany = false,
    /** 会社別の行の報酬額見込(歩合=取得×単価は実数、時給=時給×実稼働(推定)は推定)。null なら「—」 */
    companyPay: number | null = null,
    /** 速報のときの報酬額の見込を出すための行(livePayOf に渡す。D-135) */
    liveRowsForPay: StaffMetrics[] | null = null,
    /** その行の目標原価率(給湯器 or 不用品。2026-09-24) */
    target: number | null = costTarget,
  ) => {
    const d = derived(agg);
    const na = <span className="cell-na">—</span>;
    /**
     * 「オペレーターレポートにしか無い数字」を出せない行。
     *  - 架電会社別の子行(人ごとの合計しか無く会社別に割れない)
     *  - **速報の表**(CSV取込前。実稼働・受付・通話時間・後処理・報酬が API に無い。D-120)
     */
    const noOp = perCompany || isLive;
    /** 速報の行: 分母は実稼働(推定)。通話時間は締めの通話明細(CDR)にだけある(日中の速報では 0 = 「—」) */
    const liveHours = isLive ? est : null;
    const hasTalk = isLive && agg.talk_sec > 0;
    /** 子行の取得率は API の架電数を分母にする */
    const hitRate = perCompany ? (apiCalls && apiCalls > 0 ? (agg.appointments / apiCalls) * 100 : null) : d.hit_rate;
    const callFee = Math.round((agg.talk_sec / 60) * callRate);
    /** 出勤(品川・タイミー)の日次行: 丸める前の値はカーソルを合わせた吹き出しで見せる(列幅を変えない)。手修正は「手」の印(D-56) */
    const hoursTitle = shukkin
      ? `ログイン ${formatDuration(shukkin.login_sec)}(${(shukkin.login_sec / 3600).toFixed(2)}h)→拘束 ${shukkin.shift_hours ?? '—'}h ／ 休憩＋離席 ${formatDuration(shukkin.break_sec + shukkin.away_sec)}(${((shukkin.break_sec + shukkin.away_sec) / 3600).toFixed(2)}h)→休憩 ${shukkin.break_hours ?? '—'}h${shukkin.hours_source === 'manual' ? '(手修正)' : '(自動)'}`
      : undefined;
    return (
      <>
        {HOUR_BANDS.map((h, i) => {
          const n = agg.by_hour[i] ?? 0;
          // シートの条件付き書式: 3件以上は赤で塗りつぶし、0 は目立たせない
          const sheetCls = isTotal ? '' : n >= display.hour_red ? ' sheet-hot' : n === 0 ? ' sheet-zero' : '';
          return (
            <td key={h} className={`is-num is-center${i === 0 ? ' col-group' : ''}${sheetCls}`}>
              <ToneValue tone={isTotal ? (n === 0 ? 'muted' : '') : hourlyTone(n)}>{formatCount(n)}</ToneValue>
            </td>
          );
        })}
        {/* 親=オペレーターレポートの通話回数 ／ 子(会社別)=BlueBean API の記録(完了した通話)。D-108 */}
        <td className="is-num col-group" title={perCompany ? 'BlueBean の通話の記録から数えた、その会社での通話回数(完了した通話)' : undefined}>
          {perCompany ? (apiCalls === null ? na : formatCount(apiCalls)) : formatCount(agg.calls)}
        </td>
        <td className={`is-num${hitRate !== null && hitRate >= display.hit_rate_good ? ' sheet-good' : ''}`}>
          <ToneValue tone={hitRateTone(hitRate)}>{formatPercent(hitRate)}</ToneValue>
        </td>
        <td className={`is-num${d.talk_per_appt !== null && d.talk_per_appt <= display.talk_per_appt_good_min * 60 ? ' sheet-good' : ''}`}>
          {hasTalk || !noOp ? <ToneValue tone={talkPerApptTone(d.talk_per_appt)}>{formatDuration(d.talk_per_appt)}</ToneValue> : na}
        </td>
        <td className={`is-num${d.work_per_appt !== null && d.work_per_appt <= display.work_per_appt_good_min * 60 ? ' sheet-good' : ''}`}>
          {noOp ? na : <ToneValue tone={workPerApptTone(d.work_per_appt)}>{formatDuration(d.work_per_appt)}</ToneValue>}
        </td>
        <td className="is-num col-group">{noOp ? na : formatDuration(agg.reception_sec)}</td>
        {/* 受付 AVG. = 受付時間 ÷ 受付回数(2026-09-22 追加。D-124)。受付回数を持たない古い日は「—」 */}
        <td className="is-num cell-na">{noOp || isTotal ? (noOp ? na : '') : formatDuration(d.reception_avg)}</td>
        <td className="is-num">{hasTalk || !noOp ? formatDuration(agg.talk_sec) : na}</td>
        <td className="is-num cell-na">{hasTalk || !noOp ? (isTotal ? formatCurrency(callFee) : formatDuration(d.talk_avg)) : na}</td>
        <td className="is-num">{noOp ? na : formatDuration(agg.after_sec)}</td>
        <td className="is-num cell-na">{noOp || isTotal ? (noOp ? na : '') : formatDuration(d.after_avg)}</td>
        <td className="is-num">{noOp ? na : formatDuration(d.work_sec)}</td>
        <td className="is-num" title={noOp ? undefined : hoursTitle}>
          {noOp ? na : formatDecimal(agg.hours)}
          {!noOp && shukkin?.hours_source === 'manual' && <span className="mark-edited">手</span>}
        </td>
        <td className="is-num cell-na" title="会社ごとの最初〜最後の架電 × その人の係数(参考)">
          {est === null ? '—' : formatDecimal(est)}
        </td>
        <td className={`is-num${d.calls_per_hour !== null && d.calls_per_hour >= display.calls_per_hour_good ? ' sheet-good' : ''}`}>
          {perCompany ? na : isLive ? formatDecimal(liveHours !== null && liveHours > 0 ? agg.calls / liveHours : null, { digits: 0 }) : formatDecimal(d.calls_per_hour, { digits: 0 })}
        </td>
        <td className="is-num col-group">{isTotal ? '' : formatCurrency(unit)}</td>
        <td className="is-num">{formatCurrency(agg.revenue)}</td>
        {(() => {
          // 速報(CSV取込前)も **その時点までの実績**(2026-09-24 発注者指摘)。歩合=取得×単価は実数、時給は実稼働(推定)で計算しているため、
          // 翌日のオペレーターレポート取込で確定する。D-135
          const pay = isLive ? (liveRowsForPay ? livePayOf(liveRowsForPay) : null) : perCompany ? companyPay : agg.pay;
          const rate = pay === null ? null : isLive || perCompany ? (agg.revenue > 0 ? (pay / agg.revenue) * 100 : null) : d.cost_rate;
          return (
            <>
              <td className="is-num" title={isLive ? 'ここまでの実績(歩合=取得×件数単価／時給=時給×実稼働(推定))。時給の人は実稼働が推定のため、翌日のオペレーターレポート取込で確定します' : undefined}>
                {pay === null ? na : formatCurrency(pay)}
              </td>
              <td className="is-num" title={isLive ? 'ここまでの実績(売上=取得×会社単価、報酬は左の欄)。時給の人は実稼働が推定のため、翌日のオペレーターレポート取込で確定します' : undefined}>
                {rate === null && (isLive || perCompany) ? na : <ToneValue tone={costRateTone(rate, target)}>{formatPercent(rate)}</ToneValue>}
              </td>
            </>
          );
        })()}
      </>
    );
  };

  const groupTh = (label: string, span: number, help?: Help, cls = '') => (
    <th colSpan={span} scope="colgroup" className={`is-center col-group${cls ? ` ${cls}` : ''}`}>
      {help ? <HelpTip help={help}>{label}</HelpTip> : label}
    </th>
  );
  const stickyRank = { left: 0, width: W_RANK, minWidth: W_RANK, maxWidth: W_RANK } as const;
  const stickyApph = { left: W_RANK, width: W_APPH, minWidth: W_APPH, maxWidth: W_APPH } as const;
  const stickyCost = { left: W_RANK + W_APPH, width: W_COST, minWidth: W_COST, maxWidth: W_COST } as const;
  const stickyName = { left: W_RANK + W_APPH + W_COST, width: W_NAME, minWidth: W_NAME, maxWidth: W_NAME } as const;
  /** RANK のとなりに出す原価率(右端の「原価率」と同じ値。左に固定して並べ替えのときも見えるようにする) */
  /**
   * 速報の行の「1時間あたり」の分母は **実稼働(推定)**(最初の架電〜最後の架電 × その人の係数)。
   * オペレーターレポートが無い日でも 見込/h・RANK・通話/h を出すための推定値(D-121)
   */
  const liveApph = (agg: Aggregate, est: number | null) => (est !== null && est > 0 ? agg.appointments / est : null);
  const liveRank = (agg: Aggregate, est: number | null, business: BusinessType) => rankOf(liveApph(agg, est), business);

  /**
   * RANK のとなりの「取得実績」= 1時間あたりの取得数(旧名 見込/h。2026-09-22 改称・移動。D-124)。
   * 色は RANK と同じ段階。会社別の子行は稼働が会社別に割れないので「—」。速報は 実稼働(推定)で割る
   */
  /** シートの「見込取得数/h」の色分け(給湯器 <1.6 赤 / >2.2 青、不用品 ≤0.2 赤 / >0.4 青) */
  const sheetApphClass = (v: number | null, business: BusinessType | null | undefined): string => {
    if (v === null || !Number.isFinite(v)) return '';
    const [low, high] = business === 'fuyohin' ? [display.apph_lo_fuyohin, display.apph_hi_fuyohin] : [display.apph_lo, display.apph_hi];
    return v > high ? ' sheet-hi' : v < low || (business === 'fuyohin' && v <= low) ? ' sheet-lo' : '';
  };
  const apphCell = (agg: Aggregate | null, rank: Rank, est: number | null = null, blank = false, business: BusinessType | null = null) => {
    const value = agg === null ? null : isLive ? liveApph(agg, est) : derived(agg).apph;
    return (
      <td className={`is-num is-center is-sticky ${agg === null || blank ? '' : rankTextClass(rank)}${blank ? '' : sheetApphClass(value, business)}`} style={stickyApph}>
        {agg === null ? '' : blank ? <span className="cell-na">—</span> : formatDecimal(value)}
      </td>
    );
  };

  /**
   * 左に固定する原価率。速報(CSV取込前)は **報酬の見込**(歩合=取得×単価／時給=時給×実稼働(推定))で出す(D-135。それまでは「—」だった D-120)。
   * `liveRowsForPay` はその見込を出すための行
   */
  const costCell = (agg: Aggregate | null, liveRowsForPay: StaffMetrics[] | null = null, target: number | null = costTarget) => {
    if (agg === null) return <td className="is-num is-sticky" style={stickyCost} />;
    const pay = isLive ? (liveRowsForPay ? livePayOf(liveRowsForPay) : null) : agg.pay;
    const rate = pay === null || agg.revenue <= 0 ? null : (pay / agg.revenue) * 100;
    return (
      <td className="is-num is-sticky" style={stickyCost} title={isLive ? 'ここまでの実績(売上=取得×会社単価、報酬は 歩合=取得×件数単価／時給=時給×実稼働(推定))' : undefined}>
        {isLive && rate === null ? <span className="cell-na">—</span> : <ToneValue tone={costRateTone(rate, target)}>{formatPercent(rate)}</ToneValue>}
      </td>
    );
  };

  /** 取込の状態。上の行に収まるよう小さくし、くわしい中身はカーソルを合わせたときに出す(2026-09-20 発注者指示) */
  const sourceStatus =
    mode === 'day' ? (
      <span className="source-status is-inline" aria-label="取込の状態">
        <span className="source-item" title={hasOpReport ? summaries[0]!.sources!.op_report!.file_name : '稼働・通話回数は 0 のまま'}>
          <span className="source-name">OPレポート</span>
          {hasOpReport ? <StatusBadge kind="confirmed" label="取込済み" /> : <StatusBadge kind="notEntered" label="未取込" />}
        </span>
        <span
          className="source-item"
          title={activitySources.length > 0 ? activitySources.map((a) => `${a.business_codes.join('/') || '業務なし'} ${formatCount(a.appointments, { unit: '件' })}`).join('・') : '取得件数は取込後に入ります'}
        >
          <span className="source-name">対応履歴</span>
          {activitySources.length > 0 ? <StatusBadge kind="confirmed" label="取込済み" /> : <StatusBadge kind="notEntered" label="未取込" />}
        </span>
      </span>
    ) : null;

  return (
    <div className="screen full is-fill">
      <PeriodToolbar
        period={period}
        right={
          <>
            {sourceStatus}
            {isAdmin && (
              <>
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={openKintai} title={mode !== 'day' ? '日次に切り替えて、その日の出勤の勤怠を確認します' : undefined}>
                  出勤の勤怠を確認{hasOpReport ? `(${shukkinRows.length}名)` : ''}
                  {mode !== 'day' ? null : !hasOpReport ? (
                    <StatusBadge kind="noData" label="OP未取込" />
                  ) : kintaiCheck ? (
                    <StatusBadge kind="confirmed" label={`確認済み ${fmtKintaiCheck(kintaiCheck)}`} />
                  ) : (
                    <StatusBadge kind="unconfirmed" label="未確認" />
                  )}
                </button>
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setUploadOpen(true)}>
                  CSVアップロード
                </button>
              </>
            )}
          </>
        }
      />
      {uploadOpen && (
        <CsvUploadModal
          defaultDate={range.anchor}
          companies={companies}
          onClose={() => setUploadOpen(false)}
          onImported={(ds) => {
            const d = ds[ds.length - 1];
            if (d && (d !== range.anchor || mode !== 'day')) period.setMode('day');
            if (d && d !== range.anchor) period.setAnchor(d);
            void reload();
          }}
        />
      )}
      {kintaiOpen && (
        <KintaiModal
          date={range.anchor}
          rows={shukkinRows}
          hasOpReport={hasOpReport}
          checked={kintaiCheck}
          companies={companies}
          onClose={() => setKintaiOpen(false)}
          onSaved={() => {
            void reload();
          }}
        />
      )}
      {loading ? (
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      ) : sourceRows.length === 0 ? (
        <div className="ui-panel">
          <div className="ui-empty">
            {range.label} の集計データはまだ確定していません。{isAdmin && '〆作業でOPレポート・対応履歴のCSVを取り込むと、ここに確定値が出ます。'}
          </div>
        </div>
      ) : (
        <Panel
          className="fill-grow"
          title="スタッフ別"
          meta={
            isLive
              ? `${formatCount(groups.length)}名 ／ ${liveAsOf(live?.doc ?? null, live?.kind ?? '速報')}${liveForecast !== null ? ` ／ 予測(着地) ${formatCount(liveForecast, { unit: '件' })}` : ''}${liveAt ? ` ／ ${liveAt.getHours()}:${String(liveAt.getMinutes()).padStart(2, '0')} に読み込み` : ''}`
              : `${formatCount(groups.length)}名${mode !== 'day' ? '(期間内を合算)' : ''}`
          }
          note={`${isLive ? 'この日はまだCSVを取り込んでいないので、**BlueBean の記録(速報)**から出しています。**実稼働は「最初の架電〜最後の架電 × その人の係数」の推定**で、取得実績・RANK・通話/h はその推定で割った値です。**報酬実績・原価率はここまでの実績**(売上=取得×会社単価、報酬は 歩合=取得×件数単価・時給=時給×実稼働(推定))です。**時給の人は実稼働が推定のため、翌日のオペレーターレポート取込で確定**します。**受付・後処理はオペレーターレポートにしか無いので「—」**(通話時間は19時の締め以降に入ります)。' : ''}${excludedCount > 0 ? `**合計から外している人 ${excludedCount}名**(一番右のチェックを外すと合計に戻ります)。` : '一番右の「合計から外す」にチェックを付けると、その人を**合計・小計から外します**(行は薄いまま残ります)。'}行を開くと架電会社別。**親の行はオペレーターレポートの数字、会社別の行は BlueBean API の記録**です。報酬実績は 歩合=取得×単価、時給=1日ぶんの報酬を会社ごとの架電時間の比で分けたもので、**会社別を足すと親の行と同じ**になります。受付・通話時間・後処理・稼働は人ごとの合計しか無いため会社別では「—」になります`}
          actions={
            <>
              {/* 検索・絞り込みは見出しと同じ行に置く(上の情報量を減らし、アポインターごとの数字をすぐ見られるように。2026-09-20 発注者指示) */}
              <SearchBox className="ui-search is-sm" value={keyword} onChange={setKeyword} placeholder="名前・BB IDで検索" />
              <MultiSelect label="出勤形態" options={styleOptions} value={styleF} onChange={setStyleF} />
              <MultiSelect label="架電内容" options={(Object.keys(BUSINESS_LABELS) as BusinessType[]).map((k) => ({ key: k, label: BUSINESS_LABELS[k] }))} value={bizF} onChange={setBizF} />
              <MultiSelect label="架電会社" options={companyOptions.map((c) => ({ key: c.id, label: c.name }))} value={companyF} onChange={setCompanyF} />
              {excludedCount > 0 && (
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setExcluded(new Set())}>
                  合計から外した {excludedCount}名を戻す
                </button>
              )}
              <span className="panel-head-sep" aria-hidden="true" />
              <SegmentedControl
                label="架電会社別"
                size="sm"
                options={[
                  { key: 'on', label: '開ける' },
                  { key: 'off', label: '開かない' },
                ]}
                value={showByCompany ? 'on' : 'off'}
                onChange={(v) => setShowByCompany(v === 'on')}
              />
              <TableDensity value={density} onChange={setDensity} />
              {showByCompany && (
                <>
                  <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setOpened(new Set(groups.filter((x) => x.children.length > 1).map((x) => x.g.op_id)))}>
                    2社以上を開く
                  </button>
                  <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setOpened(new Set())}>
                    すべて閉じる
                  </button>
                </>
              )}
            </>
          }
        >
          <div className="report-scroll">
            <table className={`report-table is-wide${density === 'compact' ? ' is-compact' : ''} is-sheet`}>
              <caption className="sr-only">日次【集計】{range.label}。行を開くと架電会社別</caption>
              <thead>
                <tr>
                  <SortableTh label="RANK" sortKey="rank" sort={sort} onSort={toggleSort} help={HELP.rank} rowSpan={2} className="is-center is-sticky" style={stickyRank} />
                  <SortableTh label="取得実績" sortKey="apph" sort={sort} onSort={toggleSort} numeric help={{ ...HELP.apph, title: '取得実績(1時間あたりの取得数)' }} rowSpan={2} className="is-sticky is-center" style={stickyApph} />
                  <SortableTh label="原価率" sortKey="cost_rate" sort={sort} onSort={toggleSort} numeric defaultDir="asc" help={HELP.costRate} rowSpan={2} className="is-sticky" style={stickyCost} />
                  <SortableTh label="アポインター" sortKey="name" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} className="is-sticky is-sticky-edge" style={stickyName} />
                  {/* 出勤形態・報酬(単価)の列は出さない(2026-09-24 発注者指示。D-134)。出勤形態は上の絞り込みと既定の並び順に残っている */}
                  <SortableTh label="架電会社" sortKey="company" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} />
                  <SortableTh label="架電内容" sortKey="business" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} />
                  <SortableTh label="取得数" sortKey="appointments" sort={sort} onSort={toggleSort} rowSpan={2} numeric />
                  {groupTh('時間別取得件数(時台)', HOUR_BANDS.length, HELP.hourly)}
                  {groupTh('通話・取得効率', 4, undefined, 'sheet-h-red')}
                  {groupTh('BB稼働時間の内訳', 10)}
                  {groupTh('売上・報酬・原価率', 4)}
                  <th scope="col" rowSpan={2} className="is-center col-group" style={{ width: 72 }} title="チェックを付けた人は合計・小計から外れます(行は薄くなります)">
                    合計から
                    <br />
                    外す
                  </th>
                </tr>
                <tr>
                  {HOUR_BANDS.map((h, i) => (
                    <th key={h} scope="col" className={`is-num is-center${i === 0 ? ' col-group' : ''}`}>
                      {h}
                    </th>
                  ))}
                  <SortableTh label="通話回数" sortKey="calls" sort={sort} onSort={toggleSort} numeric className="col-group" help={HELP.apiCalls} />
                  <SortableTh label="取得率" sortKey="hit_rate" sort={sort} onSort={toggleSort} numeric help={HELP.hitRate} />
                  <SortableTh label="通話/取得" sortKey="talk_per_appt" sort={sort} onSort={toggleSort} numeric defaultDir="asc" help={HELP.talkPerAppt} />
                  <SortableTh label="稼働/取得" sortKey="work_per_appt" sort={sort} onSort={toggleSort} numeric defaultDir="asc" help={HELP.workPerAppt} />
                  <SortableTh label="受付" sortKey="reception" sort={sort} onSort={toggleSort} numeric className="col-group sheet-h-green" />
                  <th scope="col" className="is-num sheet-h-green">
                    AVG.
                  </th>
                  <SortableTh label="通話" sortKey="talk" sort={sort} onSort={toggleSort} numeric className="sheet-h-blue" />
                  <th scope="col" className="is-num sheet-h-blue">
                    AVG.
                  </th>
                  <SortableTh label="後処理" sortKey="after" sort={sort} onSort={toggleSort} numeric className="sheet-h-orange" />
                  <th scope="col" className="is-num sheet-h-orange">
                    AVG.
                  </th>
                  <SortableTh label="稼働合計" sortKey="work" sort={sort} onSort={toggleSort} numeric help={HELP.workSec} />
                  <SortableTh label="実稼働(h)" sortKey="hours" sort={sort} onSort={toggleSort} numeric />
                  <SortableTh
                    label="実稼働(推定)"
                    sortKey="est_hours"
                    sort={sort}
                    onSort={toggleSort}
                    numeric
                    help={{
                      title: '実稼働(推定)',
                      what: '会社ごとの「最初の架電〜最後の架電」に、その人の係数(過去の 実稼働 ÷ その幅 の中央値)を掛けた参考値。BlueBean API の取得結果がある日だけ出ます',
                      better: '左の実稼働(h)と近いほど推定が合っている',
                      source: '実測: 誤差の中央値 +0.02h・±1時間に91%(docs/specs/bluebean-api.md §14)',
                    }}
                  />
                  <SortableTh label="通話/h" sortKey="calls_per_hour" sort={sort} onSort={toggleSort} numeric />
                  <SortableTh label="会社単価" sortKey="unit" sort={sort} onSort={toggleSort} numeric className="col-group" />
                  <SortableTh label="売上実績" sortKey="revenue" sort={sort} onSort={toggleSort} numeric help={{ ...HELP.revenue, title: '売上実績' }} />
                  <SortableTh label="報酬実績" sortKey="pay" sort={sort} onSort={toggleSort} numeric help={{ ...HELP.pay, title: '報酬実績' }} />
                  <SortableTh label="原価率" sortKey="cost_rate" sort={sort} onSort={toggleSort} numeric defaultDir="asc" help={HELP.costRate} />
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 && (
                  <tr>
                    <td colSpan={colCount} className="cell-na">
                      絞り込みに該当するスタッフがいません
                    </td>
                  </tr>
                )}
                {groups.map(({ g, children }) => {
                  // 架電会社が1社だけの人は開いても同じ数字しか出ないので開けない
                  const canOpen = showByCompany && children.length > 1;
                  const open = canOpen && opened.has(g.op_id);
                  const unit = groupUnit(g);
                  const off = excluded.has(g.op_id);
                  return [
                    <tr key={g.op_id} className={`lv-0${canOpen ? ' is-clickable' : ''}${off ? ' is-excluded' : ''}`} onClick={canOpen ? () => toggleOpen(g.op_id) : undefined}>
                      <td className="is-center is-sticky" style={stickyRank}>
                        <RankBadge rank={isLive ? liveRank(g.agg, estimateOf(g.children), g.children[0]?.business ?? 'kyutoki') : g.rank} />
                      </td>
                      {apphCell(g.agg, isLive ? liveRank(g.agg, estimateOf(g.children), g.children[0]?.business ?? 'kyutoki') : g.rank, estimateOf(g.children), false, g.business)}
                      {costCell(g.agg, g.children, targetOf(g.business))}
                      <td className="is-sticky is-sticky-edge" style={stickyName}>
                        <span className="tree-cell">
                          {canOpen ? (
                            <button
                              type="button"
                              className="report-toggle"
                              aria-expanded={open}
                              aria-label={`${g.name}の架電会社別を${open ? '閉じる' : '開く'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOpen(g.op_id);
                              }}
                            >
                              <IconChevronRight size={14} />
                            </button>
                          ) : (
                            <span className="report-toggle-space" aria-hidden="true" />
                          )}
                          <span className="tree-label">{g.name}</span>
                        </span>
                      </td>
                      <td>{children.length === 1 ? companyName(children[0]!.company_id) : `${children.length}社`}</td>
                      <td>{g.businesses}</td>
                      <td className="is-num">{formatCount(g.agg.appointments)}</td>
                      {metricCells(g.agg, unit, false, shukkinOf(children), estimateOf(g.children), callsOf(g.children), false, null, g.children, targetOf(g.business))}
                      <td className="is-center col-group">
                        <input
                          type="checkbox"
                          className="row-exclude"
                          checked={off}
                          title={off ? `${g.name} を合計に戻す` : `${g.name} を合計から外す`}
                          aria-label={`${g.name} を合計から外す`}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleExcluded(g.op_id)}
                        />
                      </td>
                    </tr>,
                    ...(open
                      ? children.map((c) => (
                          <tr key={`${g.op_id}_${c.company_id ?? ''}`} className={`lv-1${off ? ' is-excluded' : ''}`}>
                            <td className="is-sticky" style={stickyRank} />
                            {apphCell(c.agg, '—', null, true)}
                            <td className="is-num is-sticky" style={stickyCost}>
                              {(() => {
                                // 速報(CSV取込前)もここまでの実績(歩合=取得×単価／時給=時給×実稼働(推定))。D-135
                                const pay = isLive ? livePayOf(c.rows) : childPay(c);
                                return pay === null || c.agg.revenue <= 0 ? (
                                  <span className="cell-na">—</span>
                                ) : (
                                  <ToneValue tone={costRateTone((pay / c.agg.revenue) * 100, targetOf(c.business))}>{formatPercent((pay / c.agg.revenue) * 100)}</ToneValue>
                                );
                              })()}
                            </td>
                            <td className="is-sticky is-sticky-edge" style={stickyName}>
                              <span className="tree-cell lv-1 cell-na" aria-label="架電会社別">
                                └
                              </span>
                            </td>
                            <td>
                              {companyName(c.company_id)}
                              {mode !== 'day' && <span className="ui-hint"> {formatCount(c.days)}日</span>}
                            </td>
                            <td>{BUSINESS_LABELS[c.business]}</td>
                            <td className="is-num">{formatCount(c.agg.appointments)}</td>
                            {metricCells(c.agg, c.unit_price, false, undefined, estimateOf(c.rows), callsOf(c.rows), true, childPay(c), c.rows, targetOf(c.business))}
                            <td className="col-group" />
                          </tr>
                        ))
                      : []),
                  ];
                })}
              </tbody>
              <tfoot>
                {subtotals.rows
                  .filter((s) => s.agg.count > 0)
                  .map((s) => (
                    <tr key={s.key} className="is-subtotal">
                      <td className="is-sticky" style={stickyRank} />
                      {apphCell(s.agg, rankOf(isLive ? liveApph(s.agg, estimateOf(s.rows)) : apphOf(s.agg), s.key === 'white' ? 'fuyohin' : 'kyutoki'), estimateOf(s.rows), false, s.key === 'white' ? 'fuyohin' : 'kyutoki')}
                      {costCell(s.agg, s.rows, s.key === 'white' ? fuyohinTarget : costTarget)}
                      <td className="is-sticky is-sticky-edge" style={stickyName}>
                        {s.label} 合計
                      </td>
                      <td colSpan={2} />
                      <td className="is-num">{formatCount(s.agg.appointments)}</td>
                      {metricCells(s.agg, null, true, undefined, estimateOf(s.rows), callsOf(s.rows), false, null, s.rows, s.key === 'white' ? fuyohinTarget : costTarget)}
                      <td className="col-group" />
                    </tr>
                  ))}
                <tr className="is-total">
                  <td className="is-center is-sticky" style={stickyRank}>
                    <RankBadge rank={rankOf(apphOf(subtotals.kyutoki), 'kyutoki')} />
                  </td>
                  {apphCell(subtotals.kyutoki, rankOf(apphOf(subtotals.kyutoki), 'kyutoki'), estimateOf(countedRows.filter((r) => r.business !== 'fuyohin')))}
                  {costCell(subtotals.kyutoki, countedRows.filter((r) => r.business !== 'fuyohin'))}
                  <td className="is-sticky is-sticky-edge" style={stickyName}>
                    合計{subtotals.hasWhite ? '(給湯器)' : ''}
                  </td>
                  <td colSpan={2} className="cell-na">
                    {filteredRows.length !== sourceRows.length ? '絞り込み後' : ''}
                  </td>
                  <td className="is-num">{formatCount(subtotals.kyutoki.appointments)}</td>
                  {metricCells(subtotals.kyutoki, null, true, undefined, estimateOf(countedRows.filter((r) => r.business !== 'fuyohin')), callsOf(countedRows.filter((r) => r.business !== 'fuyohin')), false, null, countedRows.filter((r) => r.business !== 'fuyohin'))}
                  <td className="col-group" />
                </tr>
                {subtotals.hasWhite && (
                  <tr className="is-subtotal">
                    <td className="is-sticky" style={stickyRank} />
                    {apphCell(total, rankOf(apphOf(total), 'kyutoki'), estimateOf(countedRows))}
                    {costCell(total, countedRows)}
                    <td className="is-sticky is-sticky-edge" style={stickyName}>
                      合計(不用品含む)
                    </td>
                    <td colSpan={2} />
                    <td className="is-num">{formatCount(total.appointments)}</td>
                    {metricCells(total, null, true, undefined, estimateOf(countedRows), callsOf(countedRows), false, null, countedRows)}
                    <td className="col-group" />
                  </tr>
                )}
                <tr className="is-subtotal">
                  <td className="is-sticky" style={stickyRank} />
                  {apphCell(null, '—')}
                  {costCell(null)}
                  <td className="is-sticky is-sticky-edge" style={stickyName}>
                    時間帯構成比
                  </td>
                  <td colSpan={3} />
                  {HOUR_BANDS.map((h, i) => (
                    <td key={h} className={`is-num is-center${i === 0 ? ' col-group' : ''}`}>
                      {total.appointments > 0 ? formatPercent(((total.by_hour[i] ?? 0) / total.appointments) * 100) : '—'}
                    </td>
                  ))}
                  <td colSpan={colCount - BASIC - HOUR_BANDS.length} />
                </tr>
              </tfoot>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
